import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import {
  CardMedia,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  Grid,
  Tooltip,
  IconButton,
  TablePagination,
  Skeleton,
} from '@mui/material';
import styles from './styles.module.css';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FilterList from 'components/utils/FilterList/FilterList';
import { getFilteredColumns } from 'components/utils/FilteredColumns';
import { ShopContext } from 'Context/ShopContext';
import ShopsHelper from 'helpers/ShopsHelper';

export default function DataTable(props) {
  const { shop } = useContext(ShopContext);
  const rows = useMemo(() => props.rows || [], [props.rows]);
  const rowKey = props.rowKey || 'id';
  const columns = useMemo(() => props.columns || [], [props.columns]);
  const filterId = props.columnFilterId ?? null;
  const optionalColumns = useMemo(() => props.optionalColumns || null, [props.optionalColumns]);
  const [filteredColumns, setFilteredColumns] = useState(columns);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const openTableFilter = Boolean(anchorEl);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(props.rowsPerPageOptions?.[0] || 200);

  const handleClickFilter = (event) => setAnchorEl(event.currentTarget);
  const handleCloseFilter = () => setAnchorEl(null);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getValueForCell = (data, key) => {
    if (key.includes('.')) {
      let value = data;
      key.split('.').forEach((segment) => {
        if (value) {
          value = value[segment];
        } else {
          return undefined;
        }
      });
      return value;
    } else if (key.includes('-')) {
      return key
        .split('-')
        .map((k) => data[k])
        .join('-');
    } else {
      return data[key];
    }
  };

  const getRowStyle = (row) => {
    if (props.deactiveRow && row?.is_active === false) {
      return { backgroundColor: 'rgba(245, 39, 39, 0.2)' };
    }
    return props.rowStyle?.(row) ?? {};
  };

  const getRowBlockMessage = (row) => {
    return props.rowBlockMessage?.(row) ?? null;
  };

  const handleTableFilter = (col, value) => {
    let filterData;
    const prevfilterData = localStorage.getItem(filterId);
    if (prevfilterData) {
      filterData = JSON.parse(prevfilterData);
      filterData[col.id] = value;
    } else {
      filterData = {
        [col.id]: value,
      };
    }
    localStorage.setItem(filterId, JSON.stringify(filterData));
    readFilteredColumns();
  };

  const readFilteredColumns = useCallback(() => {
    if (!filterId) return;
    const filteredColumns = getFilteredColumns(columns, optionalColumns, filterId);
    setFilteredColumns(filteredColumns);
  }, [columns, optionalColumns, filterId]);

  useEffect(() => {
    if (filterId) {
      readFilteredColumns();
    } else {
      setFilteredColumns(props.columns || []);
    }
  }, [props.columns, filterId, readFilteredColumns]);

  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= rows.length) {
      setPage(0);
    }
  }, [rows, page, rowsPerPage]);

  const displayRows = props.pagination ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : rows;

  return (
    <>
      <TableContainer style={{ marginTop: '4px', overflowX: 'auto' }} component={Paper} key={props.key || 'data-table'}>
        <Table aria-label="simple table" size="medium" style={{ width: '100%', ...props.tableStyle }}>
          <TableHead>
            <TableRow>
              {filteredColumns.map((column) => (
                <TableCell
                  key={column.id}
                  className={`${styles.columnCell} ${styles.tableCellCompact} ${column.cssClassHeader ?? ''}`}
                  align={column.align}
                  style={column.style}
                >
                  {column.label}
                </TableCell>
              ))}
              {props?.columnFilterId && (
                <>
                  <TableCell align={'right'} className={styles.tableFilter}>
                    <Tooltip title="filter columns">
                      <IconButton onClick={handleClickFilter} size="small">
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {props.isLoading
              ? [...Array(5)].map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    {filteredColumns.map((column) => {
                      let skeleton = <Skeleton variant="text" width="80%" animation="wave" />;
                      if (column.type === 'image') {
                        skeleton = (
                          <Skeleton
                            variant="rectangular"
                            width={40}
                            height={40}
                            animation="wave"
                            sx={{ borderRadius: '4px' }}
                          />
                        );
                      } else if (column.type === 'button') {
                        skeleton = (
                          <Skeleton
                            variant="rectangular"
                            width={80}
                            height={30}
                            animation="wave"
                            sx={{ borderRadius: '15px' }}
                          />
                        );
                      }
                      return (
                        <TableCell key={column.id} align={column.align}>
                          {skeleton}
                        </TableCell>
                      );
                    })}
                    {props?.columnFilterId && <TableCell />}
                  </TableRow>
                ))
              : displayRows.map((row) => (
                  <TableRow
                    key={getValueForCell(row, rowKey)}
                    style={getRowStyle(row)}
                    onClick={() => props.rowClickListener?.(row)}
                    className={styles.tableRow}
                  >
                    {filteredColumns.map((column) => {
                      const rawValue = getValueForCell(row, column.id);
                      let value = '-dff';
                      try {
                        switch (column.type) {
                          case 'text':
                            value = String(rawValue ?? '-');
                            break;
                          case 'floatQuantity':
                            value = isNaN(rawValue) ? rawValue : rawValue.toFixed(3);
                            break;
                          case 'floatAmount':
                            value =
                              !rawValue || isNaN(rawValue)
                                ? String(rawValue ?? '-')
                                : ShopsHelper.getAmountFormatted(shop, rawValue);
                            break;
                          case 'callback':
                            value = column.viewRender(row);
                            break;
                          case 'button':
                            value = (
                              <Button
                                onClick={(e) => {
                                  column.clickHandler(row);
                                  e.stopPropagation();
                                }}
                                variant="contained"
                                size="small"
                                color={column?.color ?? 'secondary'}
                              >
                                {column.title || 'Button'}
                              </Button>
                            );
                            break;
                          case 'image': {
                            const url = row[column.id];
                            const decodedUrl = url && url.includes('%25') ? decodeURIComponent(url) : url;
                            value = <CardMedia size="small" image={decodedUrl} className={styles.image} />;
                            break;
                          }
                          default:
                            value = `invalid-type '${column.type}'`;
                        }
                      } catch (error) {
                        value = error.message;
                        console.error(error);
                      }

                      return (
                        <TableCell
                          key={column.id}
                          style={{ ...(column.type === 'button' ? { maxWidth: '10px' } : {}), ...column.style }}
                          align={column.align}
                          className={`${styles.tableCellCompact} ${column.cssClass ?? ''}`}
                        >
                          {value}
                        </TableCell>
                      );
                    })}
                    {getRowBlockMessage(row) && (
                      <Grid className={styles.coverParentFull}>
                        <span>{getRowBlockMessage(row)}</span>
                      </Grid>
                    )}
                  </TableRow>
                ))}
            {props.summary && (
              <TableRow>
                {filteredColumns.map((column) => {
                  const value = props.summary[column.id];
                  return (
                    <TableCell key={column.id}>
                      <b>{value ?? ''}</b>
                    </TableCell>
                  );
                })}
              </TableRow>
            )}
            {props.children}
          </TableBody>
        </Table>
      </TableContainer>

      {props.pagination && (
        <TablePagination
          rowsPerPageOptions={props.rowsPerPageOptions || [200, 400, 500]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}

      {filterId && (
        <FilterList
          isOpen={openTableFilter}
          anchorEl={anchorEl}
          columns={columns}
          optionalColumns={optionalColumns}
          filteredColumns={filteredColumns}
          handleClose={handleCloseFilter}
          handleTableFilter={handleTableFilter}
        />
      )}
    </>
  );
}
